import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import GoalAnalysis from './GoalAnalysis';
import PortfolioSummary from './PortfolioSummary';
import GoalAlignedPortfolio from './GoalAlignedPortfolio';
import GoalSummaryTable from './GoalSummaryTable';
import AIInsights from './AIInsights';
import FundCategoryReferenceTable from './FundCategoryReferenceTable';
import RiskAnalysisDashboard from './RiskAnalysisDashboard';

const { FiArrowLeft, FiAlertTriangle, FiCheckCircle, FiInfo, FiDownload, FiCpu, FiPrinter } = FiIcons;

const Results = ({ goals, userProfile, resetSimulation, backToInput }) => {
  const [showAI, setShowAI] = useState(false);

  // Simplified Print Functionality using native browser capability
  const handlePrint = () => {
    window.print();
  };

  const getInflationRate = (goalName) => {
    const lowerGoal = goalName.toLowerCase();
    if (lowerGoal.includes('education')) return 10.5;
    if (lowerGoal.includes('health') || lowerGoal.includes('medical')) return 9.5;
    return 6.5;
  };

  const getTimeBucket = (years) => {
    if (years <= 2) return 'Short-term';
    if (years <= 3) return 'Near-term';
    if (years <= 9) return 'Medium-term';
    return 'Long-term';
  };

  const getRecommendedCategories = (years) => {
    if (years <= 2) return ['Liquid Funds', 'Ultra-short Duration Funds'];
    if (years <= 3) return ['Ultra-short Duration', 'Conservative Hybrid Funds'];
    if (years <= 9) return ['Balanced Advantage', 'Aggressive Hybrid', 'Multi-Asset Funds'];
    return ['Large-cap', 'Flexi-cap', 'Multi-cap', 'Index Funds (Nifty 50/500)'];
  };

  const getExpectedReturns = (years, riskLevel = 'Medium') => {
    if (years <= 2) return { conservative: 5, aggressive: 7 };
    if (years <= 3) return { conservative: 7, aggressive: 9 };
    if (years <= 9) return { conservative: 10, aggressive: 13 };

    const baseReturns = { conservative: 12, aggressive: 15 };
    if (riskLevel === 'High') {
      return { conservative: baseReturns.conservative + 1, aggressive: baseReturns.aggressive + 1 };
    }
    if (riskLevel === 'Low') {
      return { conservative: baseReturns.conservative - 1, aggressive: baseReturns.aggressive - 1 };
    }
    return baseReturns;
  };

  const calculateTotalRequiredSIP = () => {
    return goals.reduce((total, goal) => {
      const years = parseInt(goal.yearsFromNow);
      const currentCost = parseInt(goal.currentCost);
      const inflationRate = goal.name.toLowerCase().includes('education') ? 10.5 : 6.5;
      const futureValue = currentCost * Math.pow(1 + inflationRate / 100, years);
      const expectedReturn = goal.expectedReturn ? parseFloat(goal.expectedReturn) : (years >= 10 ? 12 : years > 3 ? 10 : 7);
      const monthlyReturn = expectedReturn / 100 / 12;
      const months = years * 12;
      const requiredSIP = (futureValue * monthlyReturn) / (Math.pow(1 + monthlyReturn, months) - 1);
      return total + requiredSIP;
    }, 0);
  };

  const totalRequiredSIP = calculateTotalRequiredSIP();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-xl shadow-lg p-6 mb-8 print:shadow-none print:border-b"
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-4 sm:space-y-0">
          <h1 className="text-3xl font-bold text-gray-900">Goal vs Returns Analysis</h1>

          {/* Action Buttons - Hidden during print */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto no-print">
            <button
              onClick={() => setShowAI(!showAI)}
              className={`flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                showAI ? 'bg-indigo-700 text-white' : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              <SafeIcon icon={FiCpu} className="text-lg" />
              <span>{showAI ? 'Refresh AI Insights' : 'Get AI Insights'}</span>
            </button>

            <button
              id="print-button"
              onClick={handlePrint}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-md hover:shadow-lg"
            >
              <SafeIcon icon={FiPrinter} className="text-lg" />
              <span>Print / Save PDF</span>
            </button>

            <button
              onClick={backToInput}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} />
              <span>Back to Input</span>
            </button>

            <button
              onClick={resetSimulation}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} />
              <span>Reset Simulation</span>
            </button>
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-400 p-4 rounded-lg">
          <div className="flex items-start space-x-3">
            <SafeIcon icon={FiInfo} className="text-yellow-600 text-xl flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-800 mb-1">Key Insights</h3>
              <p className="text-yellow-700 text-sm">
                This analysis shows how choosing the RIGHT fund category for each goal can save you years of delay or help you achieve goals earlier. Use the "Get AI Insights" button to analyze your portfolio health and risks.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Content Wrapper */}
      <div className="print-content">
        {/* AI Insights Section */}
        {showAI && (
          <AIInsights
            goals={goals}
            userProfile={userProfile}
            totalRequiredSIP={totalRequiredSIP}
          />
        )}

        {/* Portfolio Summary */}
        <PortfolioSummary goals={goals} userProfile={userProfile} />

        {/* Goal Summary Table */}
        <div id="goal-summary-table">
          <GoalSummaryTable goals={goals} />
        </div>

        {/* Goal-Aligned Portfolio */}
        <div id="goal-aligned-portfolio">
          <GoalAlignedPortfolio goals={goals} userProfile={userProfile} />
        </div>

        {/* Risk Analysis Dashboard */}
        <RiskAnalysisDashboard goals={goals} />

        {/* Individual Goal Analysis */}
        <div id="individual-goals" className="space-y-8">
          {goals.map((goal, index) => {
            const years = parseInt(goal.yearsFromNow);
            const currentCost = parseInt(goal.currentCost);
            const inflationRate = getInflationRate(goal.name);
            const timeBucket = getTimeBucket(years);
            const recommendedCategories = getRecommendedCategories(years);
            const expectedReturns = getExpectedReturns(years, userProfile.riskComfort);

            return (
              <motion.div
                key={goal.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="break-inside-avoid"
              >
                <GoalAnalysis
                  goal={goal}
                  years={years}
                  currentCost={currentCost}
                  inflationRate={inflationRate}
                  timeBucket={timeBucket}
                  recommendedCategories={recommendedCategories}
                  expectedReturns={expectedReturns}
                  userProfile={userProfile}
                />
              </motion.div>
            );
          })}
        </div>

        {/* Fund Category Reference Table */}
        <div id="fund-reference-table">
          <FundCategoryReferenceTable />
        </div>

        {/* Summary & Action Items */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white rounded-xl p-8 mt-12 break-inside-avoid"
        >
          <h2 className="text-2xl font-bold mb-6">Key Takeaways & Action Items</h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                <SafeIcon icon={FiCheckCircle} className="text-green-300" />
                <span>What to Do Right</span>
              </h3>
              <ul className="space-y-2 text-indigo-100">
                <li>• Match your goal timeframe to the right fund category</li>
                <li>• Use equity funds for goals 10+ years away</li>
                <li>• Diversify across large-cap, flexi-cap, and mid-cap for long-term goals</li>
                <li>• Consider lumpsum investments if you have surplus funds</li>
                <li>• Review your portfolio every 2-3 years</li>
                <li>• Build one unified portfolio serving multiple goals</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                <SafeIcon icon={FiAlertTriangle} className="text-orange-300" />
                <span>What to Avoid</span>
              </h3>
              <ul className="space-y-2 text-indigo-100">
                <li>• Putting long-term goals in debt funds (low returns)</li>
                <li>• Chasing last year's top-performing funds</li>
                <li>• Using high-risk funds for short-term goals</li>
                <li>• Ignoring the impact of inflation on your goals</li>
                <li>• Not considering lumpsum vs SIP strategy</li>
                <li>• Creating separate portfolios for each goal</li>
              </ul>
            </div>
          </div>

          <div className="mt-8 p-6 bg-white/10 rounded-lg border border-white/20">
            <h3 className="font-semibold mb-2">Next Steps</h3>
            <p className="text-indigo-100 text-sm mb-4">
              Review whether your current funds match the time horizon of each goal. If your long-term goals are sitting in low-return funds, you may be silently delaying them by several years. Consider both SIP and lumpsum strategies based on your cash flow availability.
            </p>
            <p className="text-xs text-indigo-200">
              <strong>Disclaimer:</strong> This is an educational simulation based on assumptions, not personalised financial advice. Please consult a SEBI-registered advisor before acting on any numbers.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Results;